import { useEffect, useState, useMemo } from 'react';
import { ApolloClient, InMemoryCache, HttpLink, from, ApolloLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { TRANSLATION_API_URL } from '../constants/translationConfig';
import { useAuthToken } from './useAuthToken';
import { isHomeEDApp, isDev } from '@constants';
import { logger } from '../utils/logger';

// Global variable to store the Apollo client instance
let globalApolloClient: ApolloClient<any> | null = null;

/**
 * Creates an auth middleware for the Apollo client
 * @param authToken The authentication token to use
 * @returns An ApolloLink middleware
 */
const authMiddleware = (authToken: string | null) =>
  new ApolloLink((operation, forward) => {
    // Set the headers for the operation
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if token exists
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    operation.setContext({ headers });
    
    return forward(operation);
  });

/**
 * Creates an error link for the Apollo client
 * @param logout The logout function to call on auth errors
 * @returns An ApolloLink error handler
 */
const errorLink = (logout?: () => void) => onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      if (isDev) {
        logger.info(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );
      }
      // If we get an auth error, log out
      if (message === 'Failed to verify the supplied jwt header.' || message === 'Not Authorised!') {
        logout?.();
      }
    });
  }
  if (isDev && networkError) {
    logger.info(`[Network error]: ${networkError}`);
  }
});

/**
 * Creates a new Apollo client for the translation service
 * @param authToken The authentication token to use
 * @param logoutFn The logout function to call on auth errors
 * @returns Apollo client instance
 */
function createTranslationClient(authToken: string | null, logoutFn?: () => void): ApolloClient<any> {
  // Create an HTTP link to the translation API
  const httpLink = new HttpLink({
    uri: TRANSLATION_API_URL
  });

  // Create the Apollo client
  return new ApolloClient({
    link: from([errorLink(logoutFn), authMiddleware(authToken).concat(httpLink)]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  });
}

/**
 * Hook to initialize the Apollo client for translation service
 * This hook should be used in a React component to initialize the Apollo client
 */
export function useTranslationClient(): void {
  const { token, hsaToken, logout } = useAuthToken();
  const [initialized, setInitialized] = useState(false);
  
  // Use the appropriate token based on the app type
  const authToken = isHomeEDApp ? hsaToken : token;
  
  // Create a memoized client instance
  const client = useMemo(() => {
    return createTranslationClient(authToken, logout);
  }, [authToken, logout]);
  
  useEffect(() => {
    // Initialize the Apollo client if it hasn't been initialized yet
    if (!globalApolloClient && !initialized) {
      globalApolloClient = client;
      setInitialized(true);
      logger.info('Translation client initialized with auth token');
    }
  }, [initialized, client]);
}

/**
 * Gets the Apollo client instance for translation service
 * @returns Apollo client instance
 */
export function getTranslationClient(): ApolloClient<any> {
  if (!globalApolloClient) {
    // If the client doesn't exist yet, create it with the token from localStorage
    // This is a fallback for when the hook hasn't been initialized yet
    const authToken = localStorage.getItem('token');
    const hsaToken = localStorage.getItem('hsaToken');
    
    // Use the appropriate token based on the app type
    const tokenToUse = isHomeEDApp ? hsaToken : authToken;
    
    globalApolloClient = createTranslationClient(tokenToUse);
    logger.info('Translation client created on demand with auth token from localStorage');
  }
  
  return globalApolloClient;
}
